/**
 * @file ContractExtractionViewContainer.tsx
 * @description Shared extraction-view atom. Owns per-category collapse
 *   state and parses the raw `extractedData` into a list of category
 *   summaries (display name + item count). The visible surface is a
 *   vertical list of `CollapsibleTierHeader` rows followed by
 *   per-category `ExtractionDataSection` bodies.
 *
 *   When `showTabs` is true, the atom additionally wraps its body in
 *   the three-tab drawer strip (`ContractDrawerTabs`) — Contract /
 *   Transfer Agreement / Live Tracking [SOON] — as required by SE-4347
 *   AC-6/AC-7 and SE-4348 AC-4/AC-7. `isArchived` suppresses the Live
 *   Tracking tab for archived rows (SE-4349 AC-8). The tabbed surface
 *   is the drawer + mobile full-page route; the Player Page Financials
 *   tab uses its own inner tab strip and leaves `showTabs` at its
 *   default `false`.
 *
 *   Consumed by:
 *   - `ContractDrawerExtractionBody` (desktop drawer) — full affordances + tabs
 *   - `ContractScreenView` (mobile full-page route) — full affordances + tabs
 *   - `ArchivedDrawerBody` (archived drawer) — read-only + 2-tab strip
 *   - `PlayerFinancialsTabView` (PR C) — read-only, no Discard / AddDoc, no tabs
 *
 *   The atom coerces `readOnly=true` whenever the caller is a
 *   non-owner of contracts so non-OWNER embeds (future or legacy)
 *   never leak mutation controls.
 * @module features/contracts/components/ContractExtractionView/ContractExtractionViewContainer
 */

import React, { memo, useCallback, useMemo, useState } from "react";

import {
  CONTRACT_END_DATE_CATEGORY_KEY,
  EXTRACTION_CATEGORY_ORDER,
  LEGACY_END_DATE_CATEGORY_KEYS,
} from "@/features/contracts/constants";
import { useContractLineItemMutations } from "@/features/contracts/hooks/useContractLineItemMutations";
import { useIsContractOwner } from "@/features/contracts/hooks/useIsContractOwner";
import type { ContractExtraction } from "@/features/contracts/types";
import { formatCategoryDisplayName } from "@/features/contracts/utils/formatCategoryDisplayName";
import ContractDrawerTabs from "@/features/contracts/components/organisms/ContractDrawerTabs";

import ContractExtractionViewView, {
  type ExtractionCategorySummary,
} from "./ContractExtractionViewView";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for {@link ContractExtractionViewContainer}. */
export interface ContractExtractionViewContainerProps {
  /** Loaded extraction (parent has verified truthy + non-empty). */
  readonly extraction: ContractExtraction;
  /** Player UUID — forwarded to mutations. Falls back to extraction.playerId when null. */
  readonly playerId: string | null;
  /** Document type — forwarded to mutations. */
  readonly documentType: string;
  /** When true, all editing affordances are suppressed. */
  readonly readOnly: boolean;
  /** Whether to render the Discard button (non-readOnly only). Default false. */
  readonly showDiscardButton?: boolean;
  /** Whether to render the "+ Add Document" action (non-readOnly only). Default false. */
  readonly showAddDocumentAction?: boolean;
  /** Invoked when the user taps Discard. */
  readonly onOpenDeleteDialog?: () => void;
  /** Invoked when the user taps "+ Add Document". */
  readonly onAddDocumentPress?: () => void;
  /**
   * When true, wrap the atom in the `ContractDrawerTabs` three-tab
   * strip (Contract / Transfer Agreement / Live Tracking [SOON]).
   * Default `false` — callers who do not want the tabs (e.g. Player
   * Page Financials which owns its own inner tab strip) opt out by
   * omitting the prop.
   */
  readonly showTabs?: boolean;
  /**
   * When true AND `showTabs` is true, the Live Tracking tab is hidden
   * so archived rows render only Contract + Transfer Agreement
   * (SE-4349 AC-8). Ignored when `showTabs` is false.
   */
  readonly isArchived?: boolean;
  /**
   * Sibling extraction surfaced under the Transfer Agreement tab when
   * `showTabs` is true. Wrappers resolve it via
   * `useContractExtraction(playerId, "TRANSFER_AGREEMENT")` and thread
   * it in so the atom can render its category list or the
   * empty-state message without owning a second Apollo subscription.
   * Pass `null` (or omit) when no transfer agreement exists for the
   * player — the tab renders a friendly empty state in that case.
   */
  readonly transferAgreementExtraction?: ContractExtraction | null;
  /**
   * When true, every category starts collapsed on mount. Passed as
   * `true` from the mobile full-page route where viewport height is
   * scarce and expanding 4–6 categories simultaneously eagerly mounts
   * their `ExtractionDataSection` bodies (perf #11 F2). Desktop
   * consumers (drawer) leave this at its `false` default so the
   * current expand-all-by-default behavior is preserved.
   */
  readonly defaultCollapsed?: boolean;
}

/**
 * Tolerant type guard matching the same canonical `{ text, isConflict,
 * confidenceNote }` shape that `ExtractionDataSection`'s parser accepts.
 * Kept in sync with `isExtractionLineItem` in `ExtractionDataSection`
 * so the count rendered in the `CollapsibleTierHeader` count badge
 * always matches the number of rows the nested section will actually
 * render. Without this, malformed server entries would inflate the
 * header count and confuse reviewers.
 * @param item - Candidate entry from a category array
 * @returns Whether the item matches the canonical line-item shape
 */
function isRenderableLineItem(item: unknown): boolean {
  if (typeof item !== "object" || item === null) return false;
  const candidate = item as Record<string, unknown>;
  return (
    typeof candidate.text === "string" &&
    typeof candidate.isConflict === "boolean" &&
    typeof candidate.confidenceNote === "string"
  );
}

/**
 * Counts the renderable items in a raw category value — i.e. entries
 * that match the canonical line-item shape. Returns zero for non-array
 * values. Mirrors the downstream `ExtractionDataSection` parser so the
 * header badge can never over-state the visible row count.
 * @param raw - Raw category value from extractedData
 * @returns The renderable item count
 */
export function countCategoryItems(raw: unknown): number {
  if (!Array.isArray(raw)) return 0;
  return raw.filter(isRenderableLineItem).length;
}

/**
 * Synthetic summary for the dedicated Contract End Date section
 * (SE-4881). The end date is NOT an `extractedData` category — it lives
 * on `extraction.endDate` — so its summary is injected rather than
 * derived. `itemCount` is 0 because the section is short-circuited to
 * `ContractEndDateSection` (it never renders a `CollapsibleTierHeader`
 * count badge).
 */
const CONTRACT_END_DATE_SUMMARY: ExtractionCategorySummary = {
  key: CONTRACT_END_DATE_CATEGORY_KEY,
  displayName: formatCategoryDisplayName(CONTRACT_END_DATE_CATEGORY_KEY),
  itemCount: 0,
};

/** Options for {@link parseCategorySummaries}. */
export interface ParseCategorySummariesOptions {
  /**
   * When true, prepend the synthetic Contract End Date section so it
   * renders FIRST (SE-4881). Only the primary contract extraction passes
   * this — the Transfer Agreement pane has no end-date section.
   */
  readonly includeContractEndDate?: boolean;
}

/**
 * Whether a category key should be excluded from the rendered list.
 * Strips the dedicated `contract_end_date` key (it is injected
 * synthetically, never derived from `extractedData`) and the legacy
 * `end_date` / `endDate` free-text keys (SE-4881 point 8) so historical
 * rows don't surface the end date twice — once in the dedicated section
 * and once as a stale free-text category.
 * @param key - Raw category key from the extractedData payload
 * @returns True when the key must be filtered out
 */
function isSuppressedCategoryKey(key: string): boolean {
  return (
    key === CONTRACT_END_DATE_CATEGORY_KEY ||
    LEGACY_END_DATE_CATEGORY_KEYS.includes(key)
  );
}

/**
 * Derives the category-summary list from a raw extractedData object
 * using a **canonical display order** — known categories (per
 * `EXTRACTION_CATEGORY_ORDER`) render first in that fixed order, with
 * any unknown keys appended in whatever order the upstream object
 * surfaces them. This guarantees the Contract drawer, mobile page,
 * and Player Page Financials tab all render the same data in the
 * same visible order regardless of how the server JSON-serialises
 * its object keys (`Object.keys` order is NOT guaranteed stable
 * across refetches or JSON round-trips).
 *
 * SE-4881: the dedicated `contract_end_date` section (when requested via
 * `options.includeContractEndDate`) is prepended as a synthetic entry,
 * and the legacy `end_date` / `endDate` free-text keys are stripped from
 * the derived list so the end date never renders twice.
 * @param extractedData - Raw extraction data keyed by category
 * @param options - Optional behavior flags
 * @returns Ordered category summaries
 */
export function parseCategorySummaries(
  extractedData: Record<string, unknown> | null | undefined,
  options?: ParseCategorySummariesOptions
): readonly ExtractionCategorySummary[] {
  const prefix = options?.includeContractEndDate
    ? [CONTRACT_END_DATE_SUMMARY]
    : [];
  if (!extractedData || typeof extractedData !== "object") return prefix;
  const knownOrdered = EXTRACTION_CATEGORY_ORDER.filter(
    key => !isSuppressedCategoryKey(key) && key in extractedData
  );
  // Alphabetical sort on the unknown bucket too — otherwise two-plus
  // unknown keys would re-inherit `Object.keys` ordering and the
  // fallback path would still flap across refetches. `localeCompare`
  // keeps locale-aware ordering stable for non-ASCII keys.
  const unknownAppended = Object.keys(extractedData)
    .filter(
      key =>
        !EXTRACTION_CATEGORY_ORDER.includes(key) &&
        !isSuppressedCategoryKey(key)
    )
    .sort((left, right) => left.localeCompare(right));
  const derived = [...knownOrdered, ...unknownAppended].map(key => ({
    key,
    displayName: formatCategoryDisplayName(key),
    itemCount: countCategoryItems(extractedData[key]),
  }));
  return [...prefix, ...derived];
}

/**
 * Stable no-op forwarded to the View as a fallback when an affordance
 * is suppressed and its handler prop is absent. The container
 * short-circuits the `showDiscardButton` / `showAddDocumentAction`
 * flags BEFORE this is reached whenever the matching handler is
 * missing, so the no-op never actually ends up wired to a visible
 * affordance — it exists only to keep the View's handler types
 * non-undefined in the type system.
 */
const NOOP = () => undefined;

/**
 * Empty state rendered under the Transfer Agreement tab when no sibling
 * extraction exists for the player. Declared as a plain render helper
 * (not a component) so it doesn't trip the one-component-per-file rule
 * and stays local to this container.
 * @returns The empty-state subtree
 */
function renderTransferAgreementEmpty() {
  return (
    <Box testID="contract-drawer-transfer-agreement-empty">
      <Stack className="gap-2 p-6">
        <Text variant="body" className="text-content-muted">
          No transfer agreement has been uploaded for this player yet.
        </Text>
      </Stack>
    </Box>
  );
}

/**
 * Container for the shared extraction atom.
 * @param props - Container props
 * @param props.extraction - Loaded extraction
 * @param props.playerId - Player UUID
 * @param props.documentType - Document type
 * @param props.readOnly - When true, suppresses every mutation affordance
 * @param props.showDiscardButton - Render Discard button (non-readOnly only)
 * @param props.showAddDocumentAction - Render "+ Add Document" action (non-readOnly only)
 * @param props.onOpenDeleteDialog - Discard handler
 * @param props.onAddDocumentPress - Add-document handler
 * @param props.showTabs - Wrap the atom in `ContractDrawerTabs` when true
 * @param props.isArchived - Hide Live Tracking tab (only read when showTabs)
 * @param props.transferAgreementExtraction - Sibling extraction for the Transfer Agreement tab
 * @param props.defaultCollapsed - When true, every category starts collapsed (mobile perf #11 F2)
 * @returns The rendered atom
 */
function ContractExtractionViewContainer({
  extraction,
  playerId,
  documentType,
  readOnly,
  showDiscardButton = false,
  showAddDocumentAction = false,
  onOpenDeleteDialog,
  onAddDocumentPress,
  showTabs = false,
  isArchived = false,
  transferAgreementExtraction = null,
  defaultCollapsed = false,
}: ContractExtractionViewContainerProps) {
  // SE-4350 defense-in-depth: non-owner callers coerce to readOnly.
  const isOwner = useIsContractOwner();
  const effectiveReadOnly = readOnly || !isOwner;

  // Perf #11 F1 (SE-4333 item N) — mount the four line-item mutations
  // ONCE here and pass the memoised bundle down through the View to
  // every `ExtractionDataSection` below. Before this hoist, each
  // section mounted its own four mutations on expand, yielding 24
  // hook mounts on a six-category contract open. Now it's four —
  // regardless of how many categories the user expands.
  const mutations = useContractLineItemMutations();

  const categories = useMemo(
    () =>
      parseCategorySummaries(
        (extraction.extractedData ?? null) as Record<string, unknown> | null,
        // SE-4881: the primary contract extraction renders the dedicated
        // Contract End Date section FIRST.
        { includeContractEndDate: true }
      ),
    [extraction.extractedData]
  );

  const transferCategories = useMemo(
    () =>
      parseCategorySummaries(
        (transferAgreementExtraction?.extractedData ?? null) as Record<
          string,
          unknown
        > | null
      ),
    [transferAgreementExtraction?.extractedData]
  );

  // Seed the collapse set with every category key when
  // `defaultCollapsed` is true so mobile consumers don't eagerly mount
  // 4–6 `ExtractionDataSection` bodies at once (perf #11 F2). Reads
  // `categories` at mount time only — subsequent category-list changes
  // do NOT re-seed the set so the user's manual open/close gestures
  // are preserved across refetches.
  const [collapsedCategories, setCollapsedCategories] = useState<
    ReadonlySet<string>
  >(() =>
    defaultCollapsed
      ? new Set(
          [
            ...parseCategorySummaries(
              (extraction.extractedData ?? null) as Record<
                string,
                unknown
              > | null
            ),
            ...parseCategorySummaries(
              (transferAgreementExtraction?.extractedData ?? null) as Record<
                string,
                unknown
              > | null
            ),
          ].map(c => c.key)
        )
      : new Set()
  );

  // The View pre-binds each per-category press handler to the stable
  // `category.key` and calls us back with that key directly (see
  // CodeRabbit Major thread on ContractExtractionViewView.tsx). No
  // reverse lookup from display label → key is needed here — the
  // callback payload IS the canonical key, which eliminates the
  // formatter-dependency the previous implementation carried.
  const handleToggleTier = useCallback((categoryKey: string) => {
    setCollapsedCategories(prev =>
      prev.has(categoryKey)
        ? new Set([...prev].filter(k => k !== categoryKey))
        : new Set([...prev, categoryKey])
    );
  }, []);

  const resolvedPlayerId = playerId ?? extraction.playerId;

  // Suppress each affordance when its handler is missing so callers
  // can't accidentally render a visible button that silently no-ops
  // when tapped. The `showDiscardButton` / `showAddDocumentAction`
  // flags alone are not sufficient — the backing handler MUST also
  // be wired. Callers who truly want a visible-but-inert button can
  // pass their own no-op handler explicitly.
  const effectiveShowDiscard =
    showDiscardButton && onOpenDeleteDialog !== undefined;
  const effectiveShowAddDocument =
    showAddDocumentAction && onAddDocumentPress !== undefined;

  const primaryBody = (
    <ContractExtractionViewView
      extraction={extraction}
      playerId={resolvedPlayerId}
      documentType={documentType}
      categories={categories}
      collapsedCategories={collapsedCategories}
      onToggleTier={handleToggleTier}
      readOnly={effectiveReadOnly}
      showDiscardButton={effectiveShowDiscard}
      showAddDocumentAction={effectiveShowAddDocument}
      onOpenDeleteDialog={onOpenDeleteDialog ?? NOOP}
      onAddDocumentPress={onAddDocumentPress ?? NOOP}
      mutations={mutations}
    />
  );

  if (!showTabs) return primaryBody;

  const transferAgreementBody = transferAgreementExtraction ? (
    <ContractExtractionViewView
      extraction={transferAgreementExtraction}
      playerId={resolvedPlayerId}
      // The Transfer Agreement pane always uses the TRANSFER_AGREEMENT
      // document type regardless of the caller's primary documentType,
      // so mutations fired from inside the pane route to the correct
      // backend bucket.
      documentType="TRANSFER_AGREEMENT"
      categories={transferCategories}
      collapsedCategories={collapsedCategories}
      onToggleTier={handleToggleTier}
      // Read-only for now — the Transfer Agreement pane is display-only
      // in this epic. SE-4347 AC-7 only requires reading Transfer
      // Agreement data; inline edit follows in a later epic.
      readOnly
      showDiscardButton={false}
      showAddDocumentAction={false}
      onOpenDeleteDialog={NOOP}
      onAddDocumentPress={NOOP}
      mutations={mutations}
    />
  ) : (
    renderTransferAgreementEmpty()
  );

  return (
    <ContractDrawerTabs
      isArchived={isArchived}
      contractSlot={primaryBody}
      transferAgreementSlot={transferAgreementBody}
    />
  );
}

ContractExtractionViewContainer.displayName = "ContractExtractionViewContainer";

export default memo(ContractExtractionViewContainer);
